import { memo, useCallback, useRef } from 'react';
import AccordionSection from './AccordionSection.jsx';
import Checkbox from './Checkbox.jsx';

/**
 * @typedef {object} AccordionSectionCheckboxListItem
 * @property {string|number} id
 * @property {import('react').ReactNode} label
 * @property {string} [indicatorColor] — CSS color for the semantic dot (e.g. `#ef4444`). When provided, a colored circle renders between the checkbox and the label. Use for Priority / Risk level variants.
 * @property {boolean} [disabled]
 */

const CheckboxItem = memo(({ id, label, checked, disabled, indicatorColor, onToggle }) => (
  <div className="flex h-7 items-center gap-2 overflow-hidden">
    <Checkbox
      checked={checked}
      disabled={disabled}
      size="small"
      aria-label={typeof label === 'string' ? label : undefined}
      onChange={() => onToggle(id)}
    />
    {indicatorColor ? (
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: indicatorColor }}
        aria-hidden
      />
    ) : null}
    <span className="min-w-0 flex-1 truncate text-sm font-medium text-content-secondary">
      {label}
    </span>
  </div>
));

/**
 * `AccordionSection` + multi-select checkbox list. Implements two Figma variants:
 * - **Simple** (no `indicatorColor`): checkbox + label — e.g. "Approved by".
 * - **With indicator** (`indicatorColor`): checkbox + color dot + label — e.g. "Risk level".
 *
 * Figma: Governance "Filter Section / Priority" + "Filter Section / Search Select" base list (node 328:16630).
 *
 * @param {import('react').ReactNode} props.sectionTitle
 * @param {boolean} [props.defaultOpen=false]
 * @param {AccordionSectionCheckboxListItem[]} [props.items=[]]
 * @param {(string|number)[]} [props.selectedIds=[]]
 * @param {(ids: (string|number)[]) => void} [props.onSelect]
 * @param {string} [props.className]
 * @param {'xs'|'sm'|'md'|'lg'} [props.titleSize]
 */
const AccordionSectionCheckboxList = ({
  sectionTitle,
  defaultOpen = false,
  items = [],
  selectedIds = [],
  onSelect,
  className = '',
  titleSize,
}) => {
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const handleToggle = useCallback(
    (id) => {
      if (typeof onSelect !== 'function') return;
      const ids = selectedIdsRef.current;
      const isSelected = ids.some((sid) => String(sid) === String(id));
      onSelect(
        isSelected
          ? ids.filter((sid) => String(sid) !== String(id))
          : [...ids, id],
      );
    },
    [onSelect],
  );

  return (
    <AccordionSection
      title={sectionTitle}
      defaultOpen={defaultOpen}
      className={className}
      titleSize={titleSize}
    >
      <div className="flex flex-col">
        {items.map((item) => (
          <CheckboxItem
            key={String(item.id)}
            id={item.id}
            label={item.label}
            checked={selectedIds.some((sid) => String(sid) === String(item.id))}
            disabled={item.disabled}
            indicatorColor={item.indicatorColor}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </AccordionSection>
  );
};

export default memo(AccordionSectionCheckboxList);
